import { FC, memo, useEffect } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { Card, Empty, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

const ITEM_HEIGHT = 250;

interface IProps {
  data: {
    value: number;
    category: string;
  }[];
  total: number;
}

const AgeGroup: FC<IProps> = ({ data, total }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!data?.length) {
      return;
    }

    const root = am5.Root.new('AgeGroup');

    // Set themes
    // https://www.amcharts.com/docs/v5/concepts/themes/
    root.setThemes([am5themes_Animated.new(root)]);

    // Create chart
    // https://www.amcharts.com/docs/v5/charts/percent-charts/pie-chart/
    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        layout: root.verticalLayout
      })
    );

    // Create series
    // https://www.amcharts.com/docs/v5/charts/percent-charts/pie-chart/#Series
    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: 'value',
        categoryField: 'category'
      })
    );

    // Set data
    // https://www.amcharts.com/docs/v5/charts/percent-charts/pie-chart/#Setting_data
    series.data.setAll(data);

    // Create legend
    // https://www.amcharts.com/docs/v5/charts/percent-charts/legend-percent-series/
    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.percent(50),
        x: am5.percent(50),
        marginTop: 15,
        marginBottom: 15
      })
    );

    legend.data.setAll(series.dataItems);

    // Play initial series animation
    // https://www.amcharts.com/docs/v5/concepts/animations/#Animation_of_series
    series.appear(1000, 100);

    return () => root.dispose();
  }, [data]);

  return (
    <Card
      title={
        <>
          <Title level={5} style={{ marginTop: 16 }}>
            {t('dashboard.age_groups')}
          </Title>
          <Title level={2} style={{ marginTop: 4 }}>
            {t('dashboard.total', { count: total })}
          </Title>
        </>
      }
    >
      {data.length ? (
        <div id="AgeGroup" style={{ height: 250 }} />
      ) : (
        <Empty style={{ height: ITEM_HEIGHT }} />
      )}
    </Card>
  );
};

export default memo(AgeGroup);
